import { useMediaQuery } from "@mui/material";
import { useIntersectionObserver, useIsClient } from "@uidotdev/usehooks";
import Cookies from "js-cookie";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import Color from "../../../assets/colors";
import { sendTrackingEvent } from "../../../helpers/tracking_management";
import {
  bookmarkJobCompany,
  unBookmarkJobCompany,
} from "../../../redux/actions/company_action";
import { updateSelectedJob } from "../../../redux/actions/job_action";
import {
  updateShowSignInModalStatus,
  updateSignUpOrigin,
} from "../../../redux/actions/navbar_action";
import { checkAuthentication } from "../../../redux/actions/user_action";
import {} from "../../error404/styles";
import {
  CompanyNameRow,
  CompanyVerifiedBadge,
} from "../../jobs/AccordionJobCard/styles";
import {
  ArrowLeftIconStyled,
  ArrowRightIconStyled,
  BookmarkFilledIconStyled,
  BookmarkIconStyled,
  ButtonStyled,
  CircularProgressStyled,
  CompanyLogo,
  FlexContainer,
  HScrollContainer,
  SectionContainer,
  TextStyled,
} from "../styles";
import {
  ButtonContainer,
  FilterChip,
  HighlightChip,
  JobCard,
  JobContainer,
  JobInfoChip,
  OverlayContainer,
  PaginationArrowSlot,
  PaginationButton,
  PaginationContainer,
  PaginationDots,
  SparklesIconStyled,
} from "./styles";

function CompanyJobs(props) {
  const { setCurrentTab, staticCompanyJobs, hasAdRail } = props || {};
  const isDesktop = useMediaQuery("(min-width: 1280px)");
  const { t } = useTranslation();
  const showSideArrows = isDesktop && !hasAdRail;
  const showBottomArrows = isDesktop && hasAdRail;

  const isClient = useIsClient();

  const [ref, entry] = useIntersectionObserver();
  const [refLeft, entryLeft] = useIntersectionObserver();
  const [refRight, entryRight] = useIntersectionObserver({
    threshold: 0.925,
    root: null,
    rootMargin: "0px",
  });

  entry?.isIntersecting ? setCurrentTab(5) : null;
  const scrollContainerRef = useRef(null);
  const showButtonLeft = entryLeft?.isIntersecting;
  const showButtonRight = entryRight?.isIntersecting;

  const reduxCompanyJobs = useSelector(
    (state) => state?.companies?.companyJobs
  );
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setHydrated(true);
  }, []);

  const companyJobs = hydrated
    ? reduxCompanyJobs || staticCompanyJobs
    : staticCompanyJobs;

  const specialisation = useSelector((state) => state?.jobs?.specialisation);

  const itemsPerPage = 9;
  const [page, setPage] = useState(1);
  const [selectedFilter, setSelectedFilter] = useState("");

  const filteredJobList =
    selectedFilter === ""
      ? companyJobs
      : companyJobs.filter((job) =>
          job.tracks.some((track) => track.id === selectedFilter.toString())
        );

  // Only paginate when hydrated
  const totalPages = hydrated
    ? Math.ceil(filteredJobList.length / itemsPerPage)
    : 1;
  const startIndex = hydrated ? (page - 1) * itemsPerPage : 0;
  const endIndex = hydrated
    ? startIndex + itemsPerPage
    : filteredJobList.length;

  const companyJobsList = filteredJobList.slice(startIndex, endIndex);

  const getSpecialisationList = useMemo(() => {
    const mapSubCategories = (categoryObj) =>
      categoryObj?.subSpecialisations?.map((subCategoryObj) => ({
        id: subCategoryObj?.id,
        parentCategory: categoryObj?.name,
        subCategory: subCategoryObj?.name,
        name: `${categoryObj?.name} - ${subCategoryObj?.name}`,
      }));
    return specialisation?.flatMap(mapSubCategories).filter(Boolean) || [];
  }, [specialisation]);

  const jobTrackList = useMemo(
    () => [
      ...new Set(
        companyJobs.flatMap((job) => job.tracks.map((track) => track.id))
      ),
    ],
    [companyJobs]
  );

  const filteredSpecialisation = useMemo(
    () =>
      getSpecialisationList.filter((track) =>
        jobTrackList.includes(track.id.toString())
      ),
    [getSpecialisationList, jobTrackList]
  );

  const lastFilterItem = useMemo(
    () => filteredSpecialisation.length - 1,
    [filteredSpecialisation]
  );

  const getSpecialisationLabel = (specialisationDetail) => {
    if (!specialisationDetail) return "";
    const formatKey = (value) =>
      value
        .toString()
        .toLowerCase()
        .replace(/&/g, "and")
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");

    const parentFallback = specialisationDetail?.parentCategory || "";
    const childFallback = specialisationDetail?.subCategory || "";

    const parentKey = parentFallback ? formatKey(parentFallback) : "";
    const parentLabel = parentKey
      ? t(`companydetail.specialisation_labels.${parentKey}`, {
          defaultValue: parentFallback,
        })
      : parentFallback;

    const childKey = childFallback ? formatKey(childFallback) : "";
    const childLabel = childKey
      ? t(`companydetail.specialisation_labels.${childKey}`, {
          defaultValue: childFallback,
        })
      : childFallback;

    if (parentLabel && childLabel) {
      return `${parentLabel} - ${childLabel}`;
    }

    return childLabel || parentLabel;
  };

  function handleFilterClick(e, specialisationDetail) {
    e.stopPropagation();

    if (specialisationDetail === "") {
      sendTrackingEvent({
        event: "CE_apply-filter-cdp",
        parent_specialization: "all",
        sub_specialization: "all",
      });
      setPage(1);
      setSelectedFilter("");
      return;
    }

    sendTrackingEvent({
      event: "CE_apply-filter-cdp",
      parent_specialization: specialisationDetail?.parentCategory,
      sub_specialization: specialisationDetail?.subCategory,
    });
    setPage(1);
    setSelectedFilter(specialisationDetail?.id);
  }

  function handleScroll(direction) {
    const scrollAmount = 800;
    const container = scrollContainerRef.current;

    if (container) {
      const currentScrollLeft = container.scrollLeft;
      let targetScrollLeft;

      if (direction === "left") {
        targetScrollLeft = currentScrollLeft - scrollAmount;
      } else if (direction === "right") {
        targetScrollLeft = currentScrollLeft + scrollAmount;
      }

      container.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth", // Add smooth scrolling behavior
      });
    }
  }

  function changePrevPage() {
    sendTrackingEvent({
      event: "CE_click-previous-job-cdp",
    });
    if (page <= 1) return;
    setPage(page - 1);
  }

  function changeNextPage() {
    sendTrackingEvent({
      event: "CE_click-next-job-cdp",
    });
    if (totalPages === page) return;
    setPage(page + 1);
  }

  return (
    <SectionContainer
      aria-label={t("companydetail.jobs_section_aria")}
      id="CompanyJobs"
      ref={ref}
    >
      <TextStyled
        variant="h2"
        fontSize="1.25rem"
        fontWeight={700}
        textTransform="capitalize"
        aria-label={t("companydetail.jobs_section_title_aria")}
        color={"#000"}
      >
        {t("companydetail.our_jobs")}
      </TextStyled>

      <FlexContainer flexDirection={"column"} margin="1rem 0">
        <TextStyled
          variant="h3"
          fontSize="1em"
          fontWeight={700}
          aria-label={t("companydetail.filter_by_specialisation_aria")}
        >
          {t("companydetail.filter_by_specialisation")}
        </TextStyled>

        {isClient && specialisation ? (
          <FlexContainer
            position="relative"
            flexDirection={"column"}
            marginTop="0.5rem"
          >
            {isDesktop && !showButtonLeft && (
              <OverlayContainer position={"left"}>
                <ButtonContainer position={"left"}>
                  <ButtonStyled
                    style={{ height: "2.25rem", width: "2.25rem" }}
                    onClick={() => handleScroll("left")}
                    id="prev-specialization-list"
                  >
                    <ArrowLeftIconStyled />
                  </ButtonStyled>
                </ButtonContainer>
              </OverlayContainer>
            )}
            {isDesktop && !showButtonRight && (
              <OverlayContainer position={"right"}>
                <ButtonContainer position={"right"}>
                  <ButtonStyled
                    style={{ height: "2.25rem", width: "2.25rem" }}
                    onClick={() => handleScroll("right")}
                    id="next-specialization-list"
                  >
                    <ArrowRightIconStyled />
                  </ButtonStyled>
                </ButtonContainer>
              </OverlayContainer>
            )}
            <HScrollContainer
              direction="row"
              gap="0.5rem"
              ref={scrollContainerRef}
            >
              <FilterChip
                selected={selectedFilter === ""}
                onClick={(e) => handleFilterClick(e, "")}
                ref={refLeft}
              >
                {t("companydetail.all")}
              </FilterChip>
              {filteredSpecialisation.map((specialisation, index) => (
                <FilterChip
                  key={specialisation.id}
                  selected={selectedFilter === specialisation.id}
                  onClick={(e) => handleFilterClick(e, specialisation)}
                  ref={lastFilterItem === index ? refRight : null}
                >
                  {getSpecialisationLabel(specialisation)}
                </FilterChip>
              ))}
            </HScrollContainer>
          </FlexContainer>
        ) : null}
      </FlexContainer>
      <FlexContainer flexDirection={"column"} alignItems="center" gap="1rem">
        <FlexContainer alignItems="center" gap="1rem" width={"100%"}>
          {showSideArrows && totalPages > 1 ? (
            totalPages >= page && page > 1 ? (
              <ButtonStyled
                onClick={changePrevPage}
                style={{ marginLeft: "-3.35rem" }}
              >
                <ArrowLeftIconStyled />
              </ButtonStyled>
            ) : null
          ) : null}
          <FlexContainer width={"100%"}>
            <JobContainer>
              {companyJobsList?.map((job, index) => {
                return <CompanyJobCard job={job} key={index} index={index} />;
              })}
            </JobContainer>
          </FlexContainer>
          {showSideArrows && totalPages > 1 ? (
            totalPages >= page && totalPages != page ? (
              <ButtonStyled
                onClick={changeNextPage}
                style={{ marginRight: "-3.35rem" }}
              >
                <ArrowRightIconStyled />
              </ButtonStyled>
            ) : null
          ) : null}
        </FlexContainer>
        {isDesktop ? (
          <PaginationContainer $withArrows={showBottomArrows && totalPages > 1}>
            {showBottomArrows && totalPages > 1 ? (
              <PaginationArrowSlot>
                {page > 1 ? (
                  <ButtonStyled onClick={changePrevPage}>
                    <ArrowLeftIconStyled />
                  </ButtonStyled>
                ) : null}
              </PaginationArrowSlot>
            ) : null}
            <PaginationDots>
              {Array.from({ length: totalPages }).map((_, index) => {
                return (
                  <PaginationButton
                    key={index}
                    style={
                      page === index + 1
                        ? { backgroundColor: Color.black }
                        : { backgroundColor: Color.white }
                    }
                  />
                );
              })}
            </PaginationDots>
            {showBottomArrows && totalPages > 1 ? (
              <PaginationArrowSlot>
                {page < totalPages ? (
                  <ButtonStyled onClick={changeNextPage}>
                    <ArrowRightIconStyled />
                  </ButtonStyled>
                ) : null}
              </PaginationArrowSlot>
            ) : null}
          </PaginationContainer>
        ) : null}
      </FlexContainer>
    </SectionContainer>
  );
}

//render company job card
function CompanyJobCard(props) {
  const { job, index } = props;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const jobTypes = useSelector((state) => state?.jobs?.jobTypes);
  const stateRegions = useSelector((state) => state?.jobs?.stateRegions);

  const [hover, setHover] = useState(false);
  const [targetJob, setTargetJob] = useState(null);
  const checkSpace = job?.title.includes(" ");

  const bookmarking = useSelector(
    (state) => state.companies.bookmarkingCompanyJob
  );
  const unbookmarking = useSelector(
    (state) => state.companies.unbookmarkingCompanyJob
  );

  const workExp = t("companydetail.years_of_experience_range", {
    min: job?.minYearsExperience,
    max: job?.maxYearsExperience,
  });

  const getJobTypeLabel = (jobType) => {
    if (!jobType) return "";
    const match = jobTypes?.find((type) => type?.title === jobType);
    const fallback = match?.slug || "";
    const keyBase = jobType;
    if (!keyBase) return fallback;
    const key = keyBase
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companydetail.job_type_labels.${key}`, {
      defaultValue: fallback,
    });
  };

  const getStateRegionLabel = (stateRegion) => {
    if (!stateRegion) return "";
    const match = stateRegions?.find((state) => state?.state === stateRegion);
    const fallback = match?.slug || "";
    const keyBase = stateRegion;
    if (!keyBase) return fallback;
    const key = keyBase
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companies.state_region_labels.${key}`, {
      defaultValue: fallback,
    });
  };

  function handleJobBookmark(e, job) {
    e.preventDefault();
    e.stopPropagation();

    const loggedIn = checkAuthentication();

    if (!loggedIn) {
      return dispatch(updateSelectedJob(job)).then(() => {
        dispatch(updateSignUpOrigin("save_job"));
        dispatch(updateShowSignInModalStatus(true));
      });
    }

    setTargetJob(job?.id);

    if (!job?.bookmark) {
      dispatch(bookmarkJobCompany({ jobId: job?.id }));
      sendTrackingEvent({
        event: "CE_save-job-cdp",
        "job-id": job?.id,
        featured: job?.boosted,
      });

      toast.success(t("companydetail.job_saved"));
      setTargetJob(null);
    } else if (job?.bookmark) {
      dispatch(unBookmarkJobCompany({ jobId: job?.id }));

      sendTrackingEvent({
        event: "CE_unsave-job-cdp",
        "job-id": job?.id,
        featured: job?.boosted,
      });

      toast.success(t("companydetail.job_unbookmarked"));
      setTargetJob(null);
    }
  }

  function handleEventJobCard(job) {
    sendTrackingEvent({
      event: "CE_click-job-card-cdp",
      job_id: job?.id,
      featured: job?.boosted,
    });

    Cookies.set("click-source", "company_jobs");
  }

  const isCompanyVerified =
    job?.company?.verified === true ||
    job?.company?.name === "Hiredly" ||
    job?.company?.name === "Hiredly X";

  return (
    <JobCard
      href={`/jobs/${job?.slug}`}
      rel="noopener noreferrer"
      target="_blank"
      onClick={() => handleEventJobCard(job)}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <FlexContainer alignItems="center" justifyContent="space-between">
        <TextStyled
          color={hover ? Color.black : "rgba(0, 0, 0, 0.60)"}
          variant="h3"
          fontSize="0.875rem"
          fontWeight={700}
          letterSpacing={"0.1px"}
          textTransform="capitalize"
          id="spec-job-title"
          sx={{ wordBreak: !checkSpace && "break-all" }}
        >
          {job?.title}
        </TextStyled>

        {job?.boosted ? (
          <HighlightChip>
            <SparklesIconStyled />
            {t("companydetail.hot")}
          </HighlightChip>
        ) : null}
      </FlexContainer>
      <FlexContainer flexDirection={"column"}>
        <FlexContainer marginTop="0.62rem" gap={"0.375rem"}>
          <JobInfoChip
            style={
              hover
                ? { color: Color.blue, background: "rgba(33, 61, 181, 0.14)" }
                : null
            }
            id="spec-job-type-chip"
          >
            {getJobTypeLabel(job?.jobType)}
          </JobInfoChip>
          <JobInfoChip
            style={
              hover
                ? {
                    color: "rgba(81, 42, 204, 1)",
                    background: "rgba(81, 42, 204, 0.14)",
                  }
                : null
            }
            id="spec-job-location-chip"
          >
            {getStateRegionLabel(job?.stateRegion)}
          </JobInfoChip>
          {job.minYearsExperience === null ||
          job.maxYearsExperience === null ||
          (job.minYearsExperience <= 0 &&
            job.maxYearsExperience === 0) ? null : (
            <JobInfoChip
              style={
                hover
                  ? {
                      color: "rgba(192, 132, 12, 1)",
                      background: "rgba(242, 175, 41, 0.14)",
                    }
                  : null
              }
              id="spec-job-exp-chip"
            >
              {workExp}
            </JobInfoChip>
          )}
        </FlexContainer>
        <FlexContainer
          alignItems="center"
          justifyContent="space-between"
          marginTop="0.88rem"
          gap="8px"
          sx={{ minWidth: 0 }}
        >
          <FlexContainer
            alignItems="center"
            gap="0.75rem"
            sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}
          >
            <CompanyLogo
              src={job?.company?.logo}
              width={26}
              height={26}
              alt={t("companydetail.company_logo_alt", {
                company: job?.company?.name,
              })}
              id="spec-job-company-logo"
            />
            <CompanyNameRow
              sx={{
                flex: 1,
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
              }}
            >
              <TextStyled
                variant="h4"
                color={Color.darkGrey}
                fontSize="0.625rem"
                fontWeight={400}
                letterSpacing="0.09375rem"
                textTransform={"uppercase"}
                textOverflow={"ellipsis"}
                whiteSpace={"nowrap"}
                overflow={"hidden"}
                sx={{ flex: "0 1 auto", minWidth: 0, display: "block" }}
                id="spec-job-company-name"
              >
                {job?.company?.name}
              </TextStyled>
              {isCompanyVerified && (
                <CompanyVerifiedBadge aria-label="Verified company" />
              )}
            </CompanyNameRow>
          </FlexContainer>
          {(bookmarking || unbookmarking) && job?.id === targetJob?.id ? (
            <CircularProgressStyled style={{ width: "20px", height: "20px" }} />
          ) : job?.bookmark ? (
            <BookmarkFilledIconStyled
              onClick={(e) => handleJobBookmark(e, job)}
              width="20px"
              height="20px"
              stroke={Color.hiredlyPurple}
              id="spec-job-bookmark"
            />
          ) : (
            <BookmarkIconStyled
              onClick={(e) => handleJobBookmark(e, job)}
              width="20px"
              height="20px"
              stroke={Color.hiredlyPurple}
              id="spec-job-bookmark"
            />
          )}
        </FlexContainer>
      </FlexContainer>
    </JobCard>
  );
}

export default CompanyJobs;
